import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import { parseProfile, type ProfileState, type SessionProfile } from '@/auth/session';

/**
 * Session + profile for the whole app.
 *
 *   loading   - the stored session is being read, or the profile is in flight
 *   signed_out
 *   active    - an approved user; `profile` is set
 *   blocked   - Supabase Auth has a session the application does not approve (invited, suspended
 *               or unknown). The session is signed out and `blockedReason` says why, so the
 *               login screen can explain instead of bouncing silently.
 *   offline   - no Supabase configuration: the prototype shell runs on sample data, as before.
 */
export type AuthStatus = 'loading' | 'signed_out' | 'active' | 'blocked' | 'offline';

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  profile: SessionProfile | null;
  blockedReason: Exclude<ProfileState, 'active' | 'signed_out'> | null;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(isSupabaseConfigured ? 'loading' : 'offline');
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<SessionProfile | null>(null);
  const [blockedReason, setBlockedReason] = useState<AuthContextValue['blockedReason']>(null);
  // The auth user whose login was last stamped, so a token refresh does not stamp it again.
  const stampedFor = useRef<string | null>(null);

  const loadProfile = useCallback(async (next: Session | null) => {
    if (!next) {
      setProfile(null);
      setStatus((current) => (current === 'blocked' ? current : 'signed_out'));
      return;
    }
    const supabase = getSupabase();
    const { data, error } = await supabase.rpc('fn_my_profile');
    if (error) {
      // Could not tell who this is. Fail closed: no profile, no shell.
      setProfile(null);
      setStatus('signed_out');
      return;
    }
    const result = parseProfile(data);
    if (result.state === 'active') {
      setBlockedReason(null);
      setProfile(result);
      setStatus('active');
      if (stampedFor.current !== next.user.id) {
        stampedFor.current = next.user.id;
        void supabase.rpc('fn_record_login');
      }
      return;
    }
    setProfile(null);
    if (result.state === 'signed_out') {
      setStatus('signed_out');
      return;
    }
    // Approved users only: drop the Auth session the application does not recognise.
    setBlockedReason(result.state);
    setStatus('blocked');
    await supabase.auth.signOut();
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    const supabase = getSupabase();
    let alive = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      void loadProfile(data.session);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, next) => {
      if (!alive) return;
      setSession(next);
      if (event === 'SIGNED_OUT') {
        stampedFor.current = null;
        setProfile(null);
        setStatus((current) => (current === 'blocked' ? current : 'signed_out'));
        return;
      }
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        // Deferred: supabase-js holds a lock while this callback runs, and an RPC made inside
        // it deadlocks the client.
        setTimeout(() => { if (alive) void loadProfile(next); }, 0);
      }
    });

    return () => {
      alive = false;
      listener.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    session,
    profile,
    blockedReason,
    refreshProfile: () => loadProfile(session),
    signOut: async () => {
      setBlockedReason(null);
      stampedFor.current = null;
      setProfile(null);
      setSession(null);
      setStatus(isSupabaseConfigured ? 'signed_out' : 'offline');
      if (!isSupabaseConfigured) return;

      // The default sign-out revokes the refresh token on the SERVER first, and when that call
      // fails - an expired session, a database that has been reset underneath it, no network in
      // the field - supabase-js leaves the stored session in place. The user is told they have
      // signed out and has not. On a shared device that is somebody else's session.
      //
      // So the local session is cleared whatever happens, and the server revocation is attempted
      // as a best effort. Local state above is already cleared, so the UI is correct either way.
      try {
        await getSupabase().auth.signOut();
      } catch {
        try {
          await getSupabase().auth.signOut({ scope: 'local' });
        } catch {
          // Nothing left to try through the client; clear the stored session by hand.
        }
      }
      try {
        for (const key of Object.keys(window.localStorage)) {
          if (key.startsWith('sb-') && key.includes('auth-token')) window.localStorage.removeItem(key);
        }
      } catch {
        // Storage can be unavailable (private mode, blocked site data); state is cleared anyway.
      }
    },
  }), [status, session, profile, blockedReason, loadProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}
